import React, { useState } from 'react';

interface AdminLoginProps {
  onLogin: (token: string) => void;
}

function AdminLogin({ onLogin }: AdminLoginProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  const startCountdown = (seconds: number) => {
    setRemainingTime(seconds);
    const interval = setInterval(() => {
      setRemainingTime(prev => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (remainingTime > 0 || loading) return;

    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      const data = await res.json();

      if (res.ok && data.token) {
        onLogin(data.token);
      } else if (res.status === 429) {
        startCountdown(data.remaining || 300);
        setError(`${data.remaining ? '' : '로그인 '}시도 초과로 잠금되었습니다`);
      } else if (res.status === 401) {
        setError(`비밀번호가 올바르지 않습니다 (${data.attempts}/${data.maxAttempts})`);
      } else {
        setError('로그인에 실패했습니다');
      }
    } catch {
      setError('서버에 연결할 수 없습니다');
    }

    setPassword('');
    setLoading(false);
  };

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const isLocked = remainingTime > 0;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-sm w-full">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-light text-gray-800 mb-2">제주로부터 관리자</h1>
          <p className="text-sm text-gray-500">비밀번호를 입력해주세요</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-8 border border-gray-200">
          <div className="mb-6">
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              placeholder="비밀번호"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-jeju-brown focus:border-transparent text-center text-lg tracking-widest disabled:bg-gray-100 disabled:cursor-not-allowed"
              autoFocus
              disabled={isLocked || loading}
            />
            {error && (
              <p className="text-red-500 text-sm mt-2 text-center">{error}</p>
            )}
            {isLocked && (
              <p className="text-red-500 text-sm mt-2 text-center">
                잠금 해제까지 {formatTime(remainingTime)}
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={isLocked || loading}
            className="w-full bg-jeju-brown text-white py-3 rounded-lg hover:bg-jeju-brown-dark transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? '확인 중...' : '로그인'}
          </button>
        </form>
        <div className="text-center mt-6">
          <a href="/" className="text-sm text-gray-400 hover:text-jeju-brown transition-colors">
            홈으로 돌아가기
          </a>
        </div>
      </div>
    </div>
  );
}

export default AdminLogin;
